import {Coupon} from "../../Types/Coupons";
import {CouponValueType} from "../../Enums/Coupons";
import {tmsToFormat} from "../Dates/DateFormat";

/**
 * Returns the unit of the coupon based on its type.
 *
 * @param {Coupon} coupon - The coupon object containing value type, value, and gifted product name.
 * @param {Object} options - Options object.
 * @param {boolean} [options.long=false] - Whether to return the long format of the unit.
 * @param {boolean} [options.max] - Whether to use the maximum value of the coupon.
 * @param {boolean} [options.min] - Whether to use the minimum value of the coupon.
 * @param {boolean} [options.showMinus] - Whether to prepend a minus sign to the value.
 * @returns {string} The unit of the coupon.
 */
export const getCouponUnit = ({couponValueType, couponValue, giftedProductName, couponMaxValue, couponMinValue}: Coupon, {long, max, min, showMinus}: {
    long?: boolean,
    max?: boolean,
    min?: boolean,
    showMinus?: boolean
} = {}): string => {
    const prepend = showMinus ? '-' : '';
    const value = max ? couponMaxValue : min ? couponMinValue : couponValue;
    if (couponValueType === CouponValueType.Percent && !max && !min) return long ? `${value}%&nbsp;de&nbsp;réduction` : `${prepend}${value}%`;
    if (couponValueType === CouponValueType.Product && !max && !min) return long ? giftedProductName : `Produit&nbsp;offert`;
    return long ? `${value}€&nbsp;de&nbsp;réduction` : `${prepend}${value}€`;
};


/**
 * Returns the details of the coupon.
 *
 * @param {Coupon} coupon - The coupon object.
 * @returns {string} The details of the coupon.
 */
export const getCouponDetails = (coupon: Coupon): string | undefined => {
    const details = [];
    if (coupon.comment) details.push(coupon.comment + '<br>');
    if (coupon.couponUsableOnlyFirstOrder) details.push('Uniquement sur la première commande.');
    if (coupon.couponMinValue) details.push(`Pour une commande de ${getCouponUnit(coupon, { min: true })} minimum.`);
    if (coupon.couponMaxValue) details.push(`Dans la limite de ${getCouponUnit(coupon, { max: true })} de réduction.`);
    if (coupon.couponNbPerclientUseable) details.push(`Utilisable ${coupon.couponNbPerclientUseable} fois.`);
    if (coupon.couponEndDate) details.push(`Expire le ${tmsToFormat(Number(coupon.couponEndDate), 'dd/MM/yyyy')}`);

    return details.length > 0 ? details.join(' ') : undefined;
}

/**
 * Returns a warning message for the coupon.
 *
 * @param {Coupon} coupon - The coupon object.
 * @returns {string | undefined} The warning message or undefined if no warnings exist.
 */
export const getCouponWarning = (coupon: Coupon): string | undefined => {
    const details = ['⚠️'];
    const isProduct = coupon.couponValueType === CouponValueType.Product;

    if (coupon.clientMaxNumber > 1 && !isProduct) details.push('Code promo en quantité limitée.');
    if (isProduct) details.push('Dans la limite des stocks disponibles.');

    return details.length > 1 ? details.join(' ') : undefined;
}
